import * as act from '@/store/settings.actions';
import { settingsService } from '@/services/settings.service';
import { EventBus } from '@/helpers/event-bus.js';
import { actions as storeActions } from '@/models/store.actions';
import { logger } from '@/helpers/log.service';
import moment from 'moment';
import { DEFAULT_DATE_FORMAT } from '@/models/defaults';
import { Languages } from '@/models/enums';

let configLoadPromiseResolver: Function;
const configLoadPromise = new Promise(resolve => {
  configLoadPromiseResolver = resolve;
});

let settLoadPromiseResolver: Function;
const settLoadPromise = new Promise(resolve => {
  settLoadPromiseResolver = resolve;
});

function getDefaultLang(): string {
  let lang = Languages.Ukrainian;
  if (navigator.language) {
    if (/^uk\b/.test(navigator.language)) {
      lang = Languages.Ukrainian;
    } else if (/^en\b/.test(navigator.language)) {
      lang = Languages.English;
    }
  }
  return lang;
}

const state = {
  locale: getDefaultLang(),
  docViewpdfWidth: 95,
  docViewshowNavigation: false,
  appSettings: null,
  config: null,
  serverVersion: '',
  documentHidenColumns: [],
  isShowOrgDocuments: undefined,
  wordWrap: false,
  serverTime: ''
};

const actions = {
  [act.ACT_DOCVIEWPDFWIDTHSET]({ commit }, { pdfWidth }): void {
    commit(act.MUT_DOCVIEWPDFWIDTHSET, pdfWidth);
  },
  [act.ACT_DOCVIEWSHOWNAVIGATIONSET]({ commit }, { showNavigation }): void {
    commit(act.MUT_DOCVIEWSHOWNAVIGATIONSET, showNavigation);
  },
  async [act.ACT_APPSET]({ commit, getters }) {
    logger.debug('settings store. start load settings');
    await getters[act.GET_CFG_WAITER];
    let sett = await settingsService.get();
    commit(act.MUT_APPSET, sett);
    logger.debug('settings store. settings loaded', sett);
  },
  async [act.ACT_CONFIG]({ commit }) {
    logger.debug('settings store. start load config');
    let cfg = await settingsService.configGet();
    commit(act.MUT_CFG, cfg);
    logger.debug('settings store. config loaded', cfg);
  },
  async [act.ACT_DOCCOLUMNS]({ commit }, { documentHidenColumns }) {
    commit(act.MUT_DOCCOLUMNS, documentHidenColumns);
  },
  async [act.ACT_WORDWRAP]({ commit }, { wordWrap }) {
    commit(act.MUT_WORDWRAP, wordWrap);
  }
};

const mutations = {
  [act.MUT_LOCALESET](state: any, locale: string): void {
    state.locale = locale;
    EventBus.$emit('locale-changed');
  },
  [act.MUT_DOCVIEWPDFWIDTHSET](state: any, pdfWidth: number): void {
    state.docViewpdfWidth = pdfWidth;
  },
  [act.MUT_DOCVIEWSHOWNAVIGATIONSET](state: any, showNavigation: boolean): void {
    state.docViewshowNavigation = showNavigation;
  },
  [act.MUT_CFG](state: any, cfg: any): void {
    state.config = cfg;
    configLoadPromiseResolver();
  },
  [act.MUT_DOCCOLUMNS](state: any, documentHidenColumns: any): void {
    state.documentHidenColumns = documentHidenColumns;
  },
  [act.MUT_APPSET](state: any, sett: any): void {
    state.appSettings = sett;
    if (state.appSettings) {
      state.appSettings.lastLoadDate = new Date();
    }
    settLoadPromiseResolver();
  },
  [act.MUT_CLEAR_OLD_STATE](state: any): void {
    state.config = {
      apiUrl: state.apiUrl
    };
    delete state.apiUrl;
  },
  [act.MUT_SERV_VERS](state: any, version: string): void {
    state.serverVersion = version;
  },
  [act.MUT_ISSHOWORGDOC](state: any, v: boolean): void {
    state.isShowOrgDocuments = v;
  },
  [act.MUT_WORDWRAP](state: any, wordWrap: any): void {
    state.wordWrap = wordWrap;
  },
  [act.MUT_SERV_TIME](state: any, serverTime: string): void {
    state.serverTime = serverTime;
  }
};

const getters = {
  [act.GET_LOCALEGET]: state => {
    return state.locale === Languages.Russian ? Languages.Ukrainian : state.locale;
  },
  [act.GET_APIURLGET]: state => {
    return state.config ? state.config.apiUrl : '';
  },
  [act.GET_APIHOSTGET]: state => {
    return state.config ? state.config.apiHost : '';
  },
  [act.GET_CURENTVERSIONGET]: state => {
    var version = process.env.VUE_APP_VERSION || JSON.parse(unescape(process.env.PACKAGE_JSON || '%7B%7D')).version;
    return version;
  },
  [act.GET_DOCVIEWPDFWIDTHGET]: state => {
    return state.docViewpdfWidth;
  },
  [act.GET_DOCVIEWSHOWNAVIGATIONGET]: state => {
    return state.docViewshowNavigation;
  },
  [act.GET_APPSET]: state => {
    return state.appSettings;
  },
  [act.GET_CFG_WAITER]: async state => {
    await configLoadPromise;
    return state.config;
  },
  [act.GET_STG_WAITER]: async state => {
    await settLoadPromise;
    return state.appSettings;
  },
  [act.GET_SERV_VERS]: state => {
    return state.serverVersion;
  },
  [act.GET_DOCCOLUMNS]: state => {
    return state.documentHidenColumns;
  },
  [act.GET_ISSHOWORGDOC]: state => {
    return state.isShowOrgDocuments;
  },
  [act.GET_WORDWRAP]: state => {
    return state.wordWrap;
  },
  [act.GET_SERV_TIME]: state => {
    let st;
    if (state.serverTime) {
      st = moment(state.serverTime, DEFAULT_DATE_FORMAT).toDate();
    }
    return st;
  }
};

export const settings = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};

export function settingsStorePlugin(store: any) {
  let { settings } = store.state;
  if ('apiUrl' in settings) {
    store.commit(storeActions.SETT_MUT_CLEAR_OLD_STATE);
  }
  delete settings.isShowMessages;
}
